import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { useState, useCallback } from "react";


const testimonials = [
  {
    name: "C.B.",
    role: "Fuel Service Franchisee",
    location: "Cape Town",
    rating: 5,
    text: "Running a petrol station means high-volume transactions daily. Their bookkeeping is meticulous and my SARS compliance has never been cleaner. Highly recommend.",
    timeAgo: "2 months ago",
  },
  {
    name: "L.F.",
    role: "Fuel Service Franchisee",
    location: "Cape Town",
    rating: 5,
    text: "Managing a fuel service station is complex. They understand the business and keep everything compliant with zero stress.",
    timeAgo: "3 months ago",
  },
  {
    name: "Dr H.",
    role: "Medical Practitioner",
    location: "Cape Town",
    rating: 5,
    text: "They handle our payroll for 3 staff, VAT, and all SARS submissions. As a busy practice, I need reliability — and they deliver every month without fail.",
    timeAgo: "1 month ago",
  },
  {
    name: "E.V.",
    role: "Coffee Shop Owner",
    location: "Cape Town",
    rating: 5,
    text: "I opened my second branch and needed proper books fast. They had me set up within a week — payroll, UIF, everything. Worth every cent.",
    timeAgo: "4 months ago",
  },
  {
    name: "A.J.",
    role: "Pharmacy Owner",
    location: "Cape Town",
    rating: 5,
    text: "Switched from doing my own books and it's been a game-changer. Professional, affordable, and they actually explain things in plain language.",
    timeAgo: "2 months ago",
  },
  {
    name: "K.H.",
    role: "Architect",
    location: "Cape Town",
    rating: 5,
    text: "Managing a practice is demanding enough. They take care of all the financials so I can focus on my projects. Couldn't ask for better service.",
    timeAgo: "5 months ago",
  },
];

const GOOGLE_REVIEW_URL = "https://www.google.com/maps/place/Bookkeeping+Services+South+Africa";

export function Testimonials() {
  const { ref, isVisible } = useScrollReveal();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Show 3 on desktop, 1 on mobile
  const getVisibleCount = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 1024) return 3;
    if (typeof window !== "undefined" && window.innerWidth >= 640) return 2;
    return 1;
  };

  const [visibleCount] = useState(getVisibleCount);
  const maxIndex = testimonials.length - visibleCount;

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-muted via-accent/20 to-muted">
      <div className="container mx-auto px-4">
        {/* Google Reviews header */}
        <div
          ref={ref}
          className={`flex flex-col sm:flex-row items-center justify-between gap-6 mb-10 max-w-6xl mx-auto scroll-reveal ${isVisible ? "revealed" : ""}`}
        >
          <div className="flex items-center gap-4">
            {/* Google "G" icon */}
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0">
              <svg viewBox="0 0 24 24" className="w-10 h-10">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </div>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                Google Reviews
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[hsl(45,93%,47%)] text-[hsl(45,93%,47%)]" />
                  ))}
                </div>
                <span className="text-sm font-semibold text-foreground">5.0</span>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative max-w-6xl mx-auto">
          {/* Navigation arrows */}
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous review"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            disabled={currentIndex >= maxIndex}
            className="absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next review"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Cards container */}
          <div className="overflow-hidden px-2">
            <div
              className="flex gap-5 transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
              }}
            >
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="flex-shrink-0 w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]"
                >
                  <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col hover:border-primary/30 hover:shadow-card-hover transition-all duration-300">
                    {/* Review header */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground shrink-0">
                        {t.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground leading-tight">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.timeAgo}</p>
                      </div>
                      {/* Google icon small */}
                      <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    </div>

                    {/* Stars */}
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[hsl(45,93%,47%)] text-[hsl(45,93%,47%)]" />
                      ))}
                    </div>

                    {/* Review text */}
                    <p className="text-sm text-foreground leading-relaxed flex-1">
                      {t.text}
                    </p>

                    {/* Role tag */}
                    <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-border">
                      {t.role} · {t.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === currentIndex
                    ? "bg-primary w-6"
                    : "bg-border hover:bg-muted-foreground"
                }`}
                aria-label={`Go to review ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          * Names abbreviated for confidentiality.
        </p>
      </div>
    </section>
  );
}

export const reviewSchema = {
  "@context": "https://schema.org",
  "@type": "AccountingService",
  "name": "Bookkeeping Services South Africa",
  "url": "https://bookkeepingservices.co.za",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5",
    "bestRating": "5",
    "worstRating": "1",
    "ratingCount": String(testimonials.length),
    "reviewCount": String(testimonials.length),
  },
  "review": testimonials.map((t) => ({
    "@type": "Review",
    "author": { "@type": "Person", "name": t.name },
    "reviewRating": { "@type": "Rating", "ratingValue": String(t.rating), "bestRating": "5" },
    "reviewBody": t.text,
  })),
};
